/**
 * The landing story (#2788): one day of the team on one screen. Behind the
 * cast, a quiet landscape from the user's transparent pieces in /site/story/
 * (a city skyline, a hospital, clouds, stars) sits under a sky that turns from morning to
 * night on the 72 s loop of the hero journeys (`heroJourney.ts`). The whole
 * cast stands in front: Maya, Jo (Alex covers at night), and the lead, Sam,
 * with both a phone and the desk.
 *
 * On its own the story plays moment by moment: the speaker lights up, says
 * their line and their screen plays; the others are muted and their screens
 * hold still. The story alone picks who is in focus. When the day ends the scene
 * holds at night under a summary: the gameplan and the four points.
 */
import { Pause, Play } from "lucide-react";
import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from "react";

import { type Cast, Character, DeskCalendar, JourneyScreen } from "./HomeStillWater";
import { OPENING_END, OpeningAssembly } from "./OpeningAssembly";
import { TariiWord, withTariiWord } from "./TariiWord";
import { LEAD_PHONE, LEAD_TRACK, LOOP_S, MEMBER_1, MEMBER_2, type Track } from "./heroJourney";
import "./roster-journey.css";
import "./opening.css";
import "./story-run.css";

type Slot = "maya" | "member2" | "leadPhone" | "desk";
type CaptionIcon = "phone" | "rules" | "roster" | "bell";

interface Moment {
  /** Seconds into the 72 s loop. */
  t: number;
  slot: Slot;
  line: string;
  /** What the log under the person keeps once the moment has passed. */
  log: string;
  tone: "req" | "ping" | "ready" | "out" | "sick";
  /** One of the four value lines, where it fits the moment. */
  caption?: { icon: CaptionIcon; title: string; text: string };
}

const MOMENTS: ReadonlyArray<Moment> = [
  {
    t: 0,
    slot: "desk",
    line: "Stations, rules, who may work where.",
    log: "Set the rules",
    tone: "ready",
    caption: {
      icon: "rules",
      title: "Your rules, set once",
      text: "Rest after a night, a fair share of nights, who may work where.",
    },
  },
  {
    t: 11,
    slot: "maya",
    line: "Asking for leave, 20 to 21 Oct.",
    log: "Asked for leave, 20 to 21 Oct",
    tone: "req",
    caption: {
      icon: "phone",
      title: "Requests from the phone",
      text: "Leave, sick days and preferences, in one place.",
    },
  },
  {
    t: 13.4,
    slot: "leadPhone",
    line: "A leave request just came in.",
    log: "Saw the leave request",
    tone: "ping",
  },
  {
    t: 25.4,
    slot: "desk",
    line: "The roster is built. Checking it now.",
    log: "Built the roster",
    tone: "ready",
    caption: {
      icon: "roster",
      title: "The roster, built for you",
      text: "Tarii builds it to your gameplan and sends it out.",
    },
  },
  {
    t: 38.8,
    slot: "leadPhone",
    line: "Published. The team has it.",
    log: "Published the roster",
    tone: "out",
  },
  {
    t: 47.8,
    slot: "member2",
    line: "I'm off sick on the 22nd.",
    log: "Reported sick, 22 Oct",
    tone: "sick",
  },
  {
    t: 48.8,
    slot: "leadPhone",
    line: "We're one short on Thu 22.",
    log: "Saw a seat short on the 22nd",
    tone: "sick",
  },
  { t: 57.6, slot: "desk", line: "Alex covers Thu 22.", log: "Covered the 22nd", tone: "ready" },
  {
    t: 58.8,
    slot: "maya",
    line: "Got my shifts. Leave confirmed.",
    log: "Got her shifts",
    tone: "out",
    caption: {
      icon: "bell",
      title: "Everyone told",
      text: "Each person sees their own shifts, and only the changes that affect them.",
    },
  },
  { t: 59.4, slot: "member2", line: "I'll take Thu 22.", log: "Covers Thu 22 Oct", tone: "out" },
];

/** The moment on stage at `s` seconds into the loop. */
/**
 * How long each moment holds the stage, in seconds of the story. The screens
 * still show each moment's own second of the journey, but no moment rushes:
 * each holds at least 6 s so a visitor can follow it.
 */
const HOLD_S: ReadonlyArray<number> = [9, 7, 7, 9, 7, 6, 7, 7, 6, 7];
const STARTS: ReadonlyArray<number> = HOLD_S.map((_, i) =>
  HOLD_S.slice(0, i).reduce((a, b) => a + b, 0),
);
/** The end card: the scene holds at night while the Tarii mark assembles. */
const END_CARD_S = OPENING_END / 1000 + 1;
/** The journey second the sky holds during the end card (deep night). */
const NIGHT_T = 63;

/** The CSS animations that follow the story's clock: the sky and its tones. */
const SKY_CLOCK = new Set(["sr-cloud-night", "sr-shadow", "sr-tone", "sr-word-tone"]);

/** One full day of the story, in seconds. */
export const STORY_S = HOLD_S.reduce((a, b) => a + b, 0);

/**
 * The moment on stage `e` seconds into the story, and the journey second the
 * sky shows then (it moves from this moment's second to the next one's).
 */
export function storyAt(e: number): { i: number; t: number } {
  let i = 0;
  STARTS.forEach((st, k) => {
    if (e >= st) i = k;
  });
  const m = MOMENTS[i];
  const next = MOMENTS[i + 1];
  const into = (e - (STARTS[i] ?? 0)) / (HOLD_S[i] ?? 1);
  const from = m?.t ?? 0;
  // The last moment eases into deep night and stays there for the end card;
  // running on to the loop's end would flash the morning first.
  const to = next ? next.t : NIGHT_T;
  return { i, t: from + (to - from) * Math.min(1, into) };
}

export function momentAt(s: number): number {
  let at = 0;
  MOMENTS.forEach((m, i) => {
    if (s >= m.t) at = i;
  });
  return at;
}

/** Clouds over the landscape: left (vw), top (%), width (vw), drift (s). */
const CLOUDS: ReadonlyArray<{ src: string; x: number; y: number; w: number; d: number }> = [
  // Clouds keep to the sides so nothing sits behind the title.
  { src: "cloud-a", x: -4, y: 8, w: 20, d: 90 },
  { src: "cloud-b", x: 3, y: 26, w: 12, d: 120 },
  { src: "cloud-a", x: 84, y: 6, w: 19, d: 100 },
  { src: "cloud-b", x: 86, y: 24, w: 12, d: 130 },
];

/** The four value icons from the old landing tiles (#2443). */
function ValueIcon({ icon }: { icon: CaptionIcon }) {
  return (
    <svg
      className="sr-icon"
      fill="none"
      stroke="#315a60"
      strokeWidth={1.8}
      viewBox="0 0 36 36"
      aria-hidden="true"
    >
      {icon === "phone" ? (
        <>
          <rect x="10" y="3" width="16" height="30" rx="3" />
          <path d="M15 29h6" />
        </>
      ) : icon === "rules" ? (
        <>
          <path d="M5 10h26M5 18h26M5 26h26" />
          <circle cx="13" cy="10" r="3" fill="#f7faf8" />
          <circle cx="23" cy="18" r="3" fill="#f7faf8" />
          <circle cx="16" cy="26" r="3" fill="#f7faf8" />
        </>
      ) : icon === "roster" ? (
        <>
          <rect x="4" y="6" width="28" height="24" rx="3" />
          <path d="M4 14h28M13 14v16M22 14v16" />
        </>
      ) : (
        <>
          <path d="M9 26V16a9 9 0 0 1 18 0v10l3 3H6z" />
          <path d="M15 31a3 3 0 0 0 6 0" />
        </>
      )}
    </svg>
  );
}

const SLOTS: ReadonlyArray<{ slot: Slot; role: string; device: "phone" | "desk"; track: Track }> = [
  { slot: "maya", role: "Member", device: "phone", track: MEMBER_1 },
  { slot: "member2", role: "Member", device: "phone", track: MEMBER_2 },
  { slot: "leadPhone", role: "Lead, phone", device: "phone", track: LEAD_PHONE },
  { slot: "desk", role: "Lead, desk", device: "desk", track: LEAD_TRACK },
];

function castOf(slot: Slot, s: number): Cast {
  if (slot === "maya") return "maya";
  // Alex takes Jo's phone for the night cover (from 59 s, as on the old stage).
  if (slot === "member2") return s >= 59 ? "alex" : "jo";
  return "sam";
}

/** The four points, in the order of the old landing tiles. */
const POINTS: ReadonlyArray<{ icon: CaptionIcon; title: string; text: string }> = [
  {
    icon: "phone",
    title: "Requests from the phone",
    text: "Leave, sick days and preferences, in one place.",
  },
  {
    icon: "rules",
    title: "Your rules, set once",
    text: "Rest after a night, a fair share of nights, who may work where.",
  },
  {
    icon: "roster",
    title: "The roster, built for you",
    text: "Tarii builds it to your gameplan and sends it out.",
  },
  {
    icon: "bell",
    title: "Everyone told",
    text: "Each person sees their own shifts, and only the changes that affect them.",
  },
];

export function StoryRun({
  reduced = false,
  children,
}: {
  /** The visitor asked for less motion: the story starts paused. */
  reduced?: boolean;
  children?: ReactNode;
}) {
  const root = useRef<HTMLElement>(null);
  // The journey second the sky shows, and the moment on stage.
  const [s, setS] = useState(0);
  const [idx, setIdx] = useState(0);
  // Milliseconds into the end card, or null while the day plays.
  const [endMs, setEndMs] = useState<number | null>(null);
  // Playing or paused. A visitor who asked for less motion starts paused.
  const [playing, setPlaying] = useState(!reduced);
  // Whether the story has ever played. From then on the scene keeps its
  // animations, and pausing only holds them still where they are.
  const [played, setPlayed] = useState(!reduced);
  const playingRef = useRef(playing);
  playingRef.current = playing;

  // The day loops while the page is open: the story, then the end card, then
  // the morning again. One frame loop drives it, and it stops while paused or
  // while the scene is off screen. The sky's CSS animations are held still
  // and set from the story's clock, so the sky never drifts from the story.
  useEffect(() => {
    const el = root.current;
    if (!el) return;
    let frame = 0;
    let elapsed = 0;
    let last = performance.now();
    let lastIdx = -1;
    let lastS = -1;
    let lastEnd: number | null = null;
    let onScreen = true;
    let sky: Animation[] | null = null;
    const io =
      typeof IntersectionObserver === "function"
        ? new IntersectionObserver(([entry]) => {
            onScreen = Boolean(entry?.isIntersecting);
          })
        : null;
    io?.observe(el);
    const skyAnimations = (): Animation[] => {
      // A browser can replace an animation (for example when the reduced-
      // motion rule toggles); a cancelled one means the list is stale.
      if (sky?.some((a) => a.playState === "idle")) sky = null;
      if (sky?.length) return sky;
      if (typeof el.getAnimations !== "function") return [];
      sky = el.getAnimations({ subtree: true }).filter((a) => {
        const name = (a as CSSAnimation).animationName ?? "";
        return name.startsWith("hp-") || SKY_CLOCK.has(name);
      });
      return sky;
    };
    const step = (now: number) => {
      const dt = (now - last) / 1000;
      last = now;
      if (playingRef.current && onScreen) elapsed += dt;
      const e = elapsed % (STORY_S + END_CARD_S);
      const inEnd = e >= STORY_S;
      const { i, t } = inEnd ? { i: MOMENTS.length - 1, t: NIGHT_T } : storyAt(e);
      for (const a of skyAnimations()) {
        a.pause?.();
        a.currentTime = t * 1000;
      }
      if (i !== lastIdx) {
        lastIdx = i;
        setIdx(i);
      }
      // React only needs the clock a few times a second.
      if (Math.abs(t - lastS) > 0.25) {
        lastS = t;
        setS(t);
      }
      const end = inEnd ? Math.round((e - STORY_S) * 1000) : null;
      if (end !== lastEnd) {
        lastEnd = end;
        setEndMs(end);
      }
      frame = window.requestAnimationFrame(step);
    };
    frame = window.requestAnimationFrame(step);
    return () => {
      window.cancelAnimationFrame(frame);
      io?.disconnect();
    };
  }, []);

  const current = MOMENTS[idx];
  // The story picks the one in focus: their light, their bubble, their screen.
  const speaker: Slot | undefined = current?.slot;
  const caption = current?.caption;

  return (
    <section
      ref={root}
      className="sr"
      aria-label="One day with Tarii"
      // Once the story has played, `data-play` lets the scene past the global
      // reduced-motion rule; paused (`data-paused`), every animation holds
      // still where it is. Never played (reduced motion, before Play), the
      // scene shows its first moment.
      data-play={played ? "" : undefined}
      data-paused={playing ? undefined : ""}
      data-end={endMs !== null ? "" : undefined}
      style={{ "--hp-loop": `${LOOP_S}s` } as CSSProperties}
    >
      <div className="sr-world" aria-hidden="true">
        <i className="sr-sky sr-sky-morning" />
        <i className="sr-sky sr-sky-day" />
        <i className="sr-sky sr-sky-dusk" />
        <i className="sr-sky sr-sky-night" />
        <div className="sr-stars" />
        {CLOUDS.map((c) => (
          <img
            key={`c-${c.x}`}
            className="sr-cloud"
            src={`/site/story/${c.src}.webp`}
            alt=""
            style={
              {
                left: `${c.x}vw`,
                top: `${c.y}%`,
                width: `${c.w}vw`,
                "--sr-drift": `${c.d}s`,
              } as CSSProperties
            }
          />
        ))}
        {/* The city: a skyline and the hospital, drawn four times (morning, day,
        dusk, night). The four cross-fade with the sky on the story's clock, so
        the windows light up as night falls. */}
        {(["morning", "day", "dusk", "night"] as const).map((time) => (
          <div key={time} className={`sr-city sr-city-${time}`}>
            <div
              className="sr-skyline"
              style={{ backgroundImage: `url(/site/story/skyline-${time}.svg)` }}
            />
            <img className="sr-hospital" src={`/site/story/hospital-${time}.svg`} alt="" />
          </div>
        ))}
      </div>

      {/* The title card in the sky: the headline, the line and the buttons. */}
      {children}

      <div className="sr-scene" aria-hidden="true">
        {SLOTS.map((sl) => {
          const speaks = speaker === sl.slot;
          const on = speaks;
          const talk = speaks ? current : undefined;
          // Every screen follows the story: at each new moment it jumps to that
          // second of its own journey. The speaker's screen plays on from
          // there; the others hold still on where their story has got to.
          const seekTo = current?.t;
          return (
            <div
              key={sl.slot}
              className={sl.device === "desk" ? "sr-slot sr-slot-desk" : "sr-slot"}
              data-speaking={on ? "" : undefined}
              // On a narrow screen the one in focus is shown large, the rest small.
              data-focus={on ? "" : undefined}
            >
              <div className="sr-person">
                {talk ? (
                  <p key={talk.t} className={`sr-say sr-say-${talk.tone}`}>
                    {talk.line}
                  </p>
                ) : null}
                <Character
                  cast={castOf(sl.slot, s)}
                  pose={on ? (sl.device === "desk" ? "point" : "phone") : "idle"}
                />
                <span className="sr-role">{sl.role}</span>
                <ol className="sr-log" aria-label={`${sl.role}: earlier lines`}>
                  {MOMENTS.slice(0, idx)
                    .filter((m) => m.slot === sl.slot)
                    .map((m) => (
                      <li key={m.t} className={`sr-log-${m.tone}`}>
                        {m.log}
                      </li>
                    ))}
                </ol>
              </div>
              {sl.device === "desk" ? (
                <div className="sr-desk">
                  <div className="hp-monitor">
                    <div className="hp-screen">
                      <JourneyScreen
                        track={sl.track}
                        reduced={false}
                        pointer="mouse"
                        paused={!on || !playing}
                        seekTo={seekTo}
                      />
                    </div>
                    <span className="hp-neck" />
                    <span className="hp-foot" />
                  </div>
                  <div className="hp-desk">
                    <DeskCalendar />
                  </div>
                </div>
              ) : (
                <div className="hp-phone">
                  <JourneyScreen
                    track={sl.track}
                    reduced={false}
                    pointer="finger"
                    paused={!on || !playing}
                    seekTo={seekTo}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {MOMENTS.map((m) =>
        m.caption ? (
          <div
            key={`${m.t}-cap`}
            className="sr-caption"
            data-on={caption === m.caption ? "" : undefined}
            aria-hidden={caption === m.caption ? undefined : true}
          >
            <ValueIcon icon={m.caption.icon} />
            {/* During the story the card shows the header only; the full
            line is in the section below the scene. */}
            <h3>{m.caption.title}</h3>
          </div>
        ) : null,
      )}

      {endMs !== null ? (
        <div className="sr-end" aria-hidden="true">
          <OpeningAssembly t={endMs} />
        </div>
      ) : null}

      <button
        type="button"
        className="sr-toggle"
        aria-label={playing ? "Pause the story" : "Play the story"}
        title={playing ? "Pause the story" : "Play the story"}
        onClick={() => {
          setPlayed(true);
          setPlaying((p) => !p);
        }}
      >
        {playing ? (
          <Pause className="sr-toggle-icon" aria-hidden="true" />
        ) : (
          <Play className="sr-toggle-icon" aria-hidden="true" />
        )}
      </button>

      <p className="sr-sr-only">
        {`The story runs ${STORY_S} seconds: a member asks for leave, the lead reviews it, the roster is built and published, a member reports off sick, the lead covers the day, and every phone is told.`}
      </p>
    </section>
  );
}

/**
 * The summary under the story (#2788): the gameplan and the four points. The
 * story keeps looping above it.
 */
export function StorySummary() {
  return (
    <section className="sr-summary" aria-labelledby="hsw-decision">
      <div className="sr-panel-top">
        <h2 id="hsw-decision">
          You set the gameplan.
          <br />
          <TariiWord /> runs the scheduling.
        </h2>
        <div className="sr-summary-text">
          <p>
            Every team runs differently. Set out how yours works: who covers which shifts, rest
            after a night, a fair share of weekends, what happens when someone calls in sick.{" "}
            <TariiWord /> collects the requests, builds each roster to that plan and sends everyone
            their shifts.
          </p>
          <p>
            Members see their own shifts and nothing else. The team lead sees the team. Nobody is
            scored.
          </p>
        </div>
      </div>
      <ul className="sr-points">
        {POINTS.map((pt) => (
          <li key={pt.title}>
            <ValueIcon icon={pt.icon} />
            <div>
              <h3>{pt.title}</h3>
              <p>{withTariiWord(pt.text)}</p>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
